/**
 * Client-side Rate Limiter
 * Prevents brute force attacks on authentication endpoints
 *
 * Usage:
 *   import { authRateLimiter } from '../utils/rateLimiter';
 *
 *   const { limited, retryAfter } = authRateLimiter.isRateLimited(email);
 *   if (limited) {
 *     setError(`Too many attempts. Try again in ${retryAfter} seconds.`);
 *     return;
 *   }
 *   authRateLimiter.recordAttempt(email);
 */

class RateLimiter {
  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    this.attempts = {};
  }

  /**
   * Check if a key is rate limited
   * @param {string} key - Identifier (usually email or IP)
   * @returns {{ limited: boolean, retryAfter?: number }}
   */
  isRateLimited(key) {
    const normalizedKey = key.toLowerCase().trim();
    const now = Date.now();
    const userAttempts = this.attempts[normalizedKey] || [];

    // Filter attempts within the time window
    const recentAttempts = userAttempts.filter((timestamp) => now - timestamp < this.windowMs);

    // Update stored attempts with only recent ones
    this.attempts[normalizedKey] = recentAttempts;

    if (recentAttempts.length >= this.maxAttempts) {
      const oldestAttempt = recentAttempts[0];
      const retryAfter = Math.ceil((oldestAttempt + this.windowMs - now) / 1000);
      return { limited: true, retryAfter };
    }

    return { limited: false };
  }

  /**
   * Record an attempt for a key
   * @param {string} key - Identifier (usually email or IP)
   */
  recordAttempt(key) {
    const normalizedKey = key.toLowerCase().trim();
    if (!this.attempts[normalizedKey]) {
      this.attempts[normalizedKey] = [];
    }
    this.attempts[normalizedKey].push(Date.now());
  }

  /**
   * Reset attempts for a key (call on successful login)
   * @param {string} key - Identifier (usually email or IP)
   */
  reset(key) {
    const normalizedKey = key.toLowerCase().trim();
    delete this.attempts[normalizedKey];
  }

  /**
   * Get remaining attempts for a key
   * @param {string} key - Identifier
   * @returns {number}
   */
  getRemainingAttempts(key) {
    const normalizedKey = key.toLowerCase().trim();
    const now = Date.now();
    const userAttempts = this.attempts[normalizedKey] || [];
    const recentAttempts = userAttempts.filter((timestamp) => now - timestamp < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

// Auth rate limiter: 5 attempts per minute
export const authRateLimiter = new RateLimiter(5, 60000);

// OTP rate limiter: 3 OTP requests per 5 minutes
export const otpRateLimiter = new RateLimiter(3, 5 * 60000);

// Password reset rate limiter: 3 requests per 15 minutes
export const passwordResetRateLimiter = new RateLimiter(3, 15 * 60000);

export default RateLimiter;
